import { buttonClasses } from '@mui/material/Button';
import type { Components } from '@mui/material/styles';

import type { Theme } from '../types';

function getContainedVars(color: string): Record<string, string> {
  return {
    '--Button-containedBg': `var(--mui-palette-${color}-main)`,
    '--Button-containedHoverBg': `var(--mui-palette-${color}-dark)`,
  };
}

function getOutlinedVars(color: string, dark: boolean): Record<string, string> {
  if (color === 'secondary') {
    return {
      '--Button-outlinedBorder': dark
        ? 'var(--mui-palette-neutral-700)'
        : 'var(--mui-palette-divider)',
      '--Button-outlinedHoverBg': 'var(--mui-palette-background-level1)',
      '--Button-outlinedActiveBg': 'var(--mui-palette-background-level2)',
      color: dark
        ? 'var(--mui-palette-common-white)'
        : 'var(--mui-palette-text-primary)',
    };
  }

  return {
    '--Button-outlinedBorder': `rgba(var(--mui-palette-${color}-mainChannel) / ${
      dark ? '0.45' : '0.24'
    })`,
    '--Button-outlinedHoverBg': `rgba(var(--mui-palette-${color}-mainChannel) / 0.08)`,
    '--Button-outlinedActiveBg': `rgba(var(--mui-palette-${color}-mainChannel) / 0.14)`,
    color: `var(--mui-palette-${color}-main)`,
  };
}

function getTextVars(color: string): Record<string, string> {
  return {
    '--Button-textHoverBg': `var(--mui-palette-${color}-hovered)`,
    '--Button-textActiveBg': `var(--mui-palette-${color}-activated)`,
  };
}

export const MuiButton = {
  defaultProps: { disableRipple: true },
  styleOverrides: {
    root: ({ theme }) => ({
      '--Button-minHeight': '36px',
      '--Button-paddingInline': '16px',
      borderRadius: '8px',
      minHeight: 'var(--Button-minHeight)',
      minWidth: 'unset',
      paddingInline: 'var(--Button-paddingInline)',
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: 'none',
      transition: theme.transitions.create(
        ['background-color', 'border-color', 'box-shadow', 'color'],
        {
          duration: theme.transitions.duration.shorter,
        }
      ),
      '&:focus-visible': {
        outline: 'none',
        boxShadow: `0 0 0 3px rgba(${theme.vars.palette.primary.mainChannel} / 0.18)`,
      },
    }),
    text: {
      '&:hover': { backgroundColor: 'var(--Button-textHoverBg)' },
      '&:active': { backgroundColor: 'var(--Button-textActiveBg)' },
    },
    textPrimary: getTextVars('primary'),
    textSecondary: getTextVars('secondary'),
    textSuccess: getTextVars('success'),
    textInfo: getTextVars('info'),
    textWarning: getTextVars('warning'),
    textError: getTextVars('error'),
    outlined: ({ theme }) => ({
      backgroundColor: 'var(--mui-palette-background-paper)',
      boxShadow: theme.shadows[1],
      borderColor: 'var(--Button-outlinedBorder)',
      '&:hover': {
        borderColor: 'var(--Button-outlinedBorder)',
        backgroundColor: 'var(--Button-outlinedHoverBg)',
        boxShadow: theme.shadows[1],
      },
      '&:active': { backgroundColor: 'var(--Button-outlinedActiveBg)' },
    }),
    outlinedPrimary: ({ theme }) => {
      return getOutlinedVars('primary', theme.palette.mode === 'dark');
    },
    outlinedSecondary: ({ theme }) => {
      return getOutlinedVars('secondary', theme.palette.mode === 'dark');
    },
    outlinedSuccess: ({ theme }) => {
      return getOutlinedVars('success', theme.palette.mode === 'dark');
    },
    outlinedInfo: ({ theme }) => {
      return getOutlinedVars('info', theme.palette.mode === 'dark');
    },
    outlinedWarning: ({ theme }) => {
      return getOutlinedVars('warning', theme.palette.mode === 'dark');
    },
    outlinedError: ({ theme }) => {
      return getOutlinedVars('error', theme.palette.mode === 'dark');
    },
    contained: ({ theme }) => ({
      backgroundColor: 'var(--Button-containedBg)',
      backgroundImage: 'none',
      border: '1px solid transparent',
      boxShadow: theme.shadows[1],
      overflow: 'hidden',
      '&:hover': {
        backgroundColor:
          'var(--Button-containedHoverBg, var(--Button-containedBg))',
        boxShadow: theme.shadows[2],
      },
      '&:active': { boxShadow: theme.shadows[1] },
      '&:focus-visible': {
        boxShadow: `${theme.shadows[1]}, 0 0 0 3px rgba(${theme.vars.palette.primary.mainChannel} / 0.18)`,
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundImage: 'none',
        boxShadow: 'none',
      },
    }),
    containedPrimary: getContainedVars('primary'),
    containedSecondary: getContainedVars('secondary'),
    containedSuccess: getContainedVars('success'),
    containedInfo: getContainedVars('info'),
    containedWarning: getContainedVars('warning'),
    containedError: getContainedVars('error'),
    sizeSmall: {
      '--Button-minHeight': '32px',
      '--Button-paddingInline': '12px',
      fontSize: '0.8125rem',
    },
    sizeMedium: {
      '--Button-minHeight': '36px',
      '--Button-paddingInline': '16px',
      fontSize: '0.875rem',
    },
    sizeLarge: {
      '--Button-minHeight': '40px',
      '--Button-paddingInline': '24px',
      fontSize: '0.9375rem',
    },
  },
} satisfies Components<Theme>['MuiButton'];
